// One way to show a time of day to customers (owner, 2 Nov 2026):
// 12-hour "1:10 pm" (lowercase am/pm, no leading zero) in Sydney/Melbourne time
// (Australia/Sydney: AEDT in daylight saving, AEST in winter), never the
// viewer's own clock.
//
// Inputs:
// - instants: Date, epoch ms, ISO strings ("2026-11-02T06:00:00.000Z"), Firestore
//   timestamps ({ seconds } / { _seconds }). Betfair marketStartTime is UTC.
// - bare "HH:mm" / "HH:mm:ss" strings: the tips backend's tipUpdatedAt is Brisbane
//   wall-clock time (AEST, UTC+10, no daylight saving). Checked 2 Nov 2026: Late Mail
//   "15:55" for Ballarat R8 (marketStartTime 06:00Z = 4:00 pm AEST = 5:00 pm AEDT),
//   and Early Mail "11:03" = updatedAt 01:03Z. Pass `on` (the day it happened, e.g.
//   the race date) so the right Sydney offset is used.

export const DISPLAY_TIME_ZONE = "Australia/Sydney";
export const BACKEND_WALL_CLOCK_TIME_ZONE = "Australia/Brisbane";

const HH_MM = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;
const DD_MM_YYYY = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
const YYYY_MM_DD = /^(\d{4})-(\d{2})-(\d{2})$/;

const formatters = new Map();
const formatter = (key, timeZone, options, locale = "en-US") => {
  const id = `${key}|${timeZone}`;
  if (!formatters.has(id)) {
    formatters.set(id, new Intl.DateTimeFormat(locale, { timeZone, ...options }));
  }
  return formatters.get(id);
};

const isValidZone = (timeZone) => {
  if (!timeZone) return false;
  try {
    formatter("probe", timeZone, {});
    return true;
  } catch {
    return false;
  }
};

const zoneOrDefault = (timeZone) =>
  isValidZone(timeZone) ? timeZone : DISPLAY_TIME_ZONE;

const validDate = (d) => (d instanceof Date && !Number.isNaN(d.getTime()) ? d : null);

/** Date, epoch ms, ISO string or Firestore timestamp -> Date (or null). Not "HH:mm". */
export function toInstant(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) return validDate(value);
  if (typeof value === "number") return validDate(new Date(value));
  if (typeof value === "object") {
    const seconds = value.seconds ?? value._seconds;
    if (typeof seconds === "number") {
      const nanos = value.nanoseconds ?? value._nanoseconds ?? 0;
      return validDate(new Date(seconds * 1000 + nanos / 1e6));
    }
    return null;
  }
  if (typeof value === "string") {
    const raw = value.trim();
    if (!raw || HH_MM.test(raw)) return null;
    return validDate(new Date(raw));
  }
  return null;
}

/** Calendar fields of an instant as seen in a time zone. */
function zonedParts(date, timeZone) {
  const parts = formatter("parts", timeZone, {
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(date);
  const out = {};
  for (const p of parts) if (p.type !== "literal") out[p.type] = Number(p.value);
  if (out.hour === 24) out.hour = 0;
  return out;
}

/** Milliseconds the zone is ahead of UTC at that instant. */
function zoneOffsetMs(date, timeZone) {
  const p = zonedParts(date, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(date.getTime() / 1000) * 1000;
}

/** { year, month, day } for `on` ("DD-MM-YYYY", "YYYY-MM-DD" or any instant) in a zone. */
function calendarDay(on, timeZone) {
  if (typeof on === "string") {
    const raw = on.trim();
    let m = DD_MM_YYYY.exec(raw);
    if (m) return { year: Number(m[3]), month: Number(m[2]), day: Number(m[1]) };
    m = YYYY_MM_DD.exec(raw);
    if (m) return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
  }
  const instant = toInstant(on) || new Date();
  const { year, month, day } = zonedParts(instant, timeZone);
  return { year, month, day };
}

/**
 * Whole calendar days from `from` to `to` as seen in `timeZone` (Sydney by default).
 * Each side may be an instant or a date-only "DD-MM-YYYY" / "YYYY-MM-DD". null if unusable.
 */
export function calendarDaysBetween(from, to, timeZone = DISPLAY_TIME_ZONE) {
  const isDay = (v) =>
    typeof v === "string" && (DD_MM_YYYY.test(v.trim()) || YYYY_MM_DD.test(v.trim()));
  if (!isDay(from) && !toInstant(from)) return null;
  if (!isDay(to) && !toInstant(to)) return null;
  const zone = zoneOrDefault(timeZone);
  const a = calendarDay(from, zone);
  const b = calendarDay(to, zone);
  return Math.round(
    (Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) / 86400000,
  );
}

/**
 * A wall-clock "HH:mm" (or "HH:mm:ss") in `timeZone` on the day `on` -> Date.
 * Defaults: Brisbane wall clock (the tips backend), today.
 */
export function wallClockToInstant(
  time,
  { on, timeZone = BACKEND_WALL_CLOCK_TIME_ZONE } = {},
) {
  const m = HH_MM.exec(String(time ?? "").trim());
  if (!m) return null;
  const [h, min, s] = [m[1], m[2], m[3] ?? "0"].map(Number);
  if (h > 23 || min > 59 || s > 59) return null;
  const zone = isValidZone(timeZone) ? timeZone : BACKEND_WALL_CLOCK_TIME_ZONE;
  const { year, month, day } = calendarDay(on, zone);
  const asUtc = Date.UTC(year, month - 1, day, h, min, s);
  // Two passes so a time next to a daylight-saving change lands on the right offset.
  let ts = asUtc - zoneOffsetMs(new Date(asUtc), zone);
  ts = asUtc - zoneOffsetMs(new Date(ts), zone);
  return validDate(new Date(ts));
}

/** "AEDT" / "AEST" (for Sydney) at that instant; defaults to now. */
export function timeZoneLabel(value = new Date(), timeZone = DISPLAY_TIME_ZONE) {
  const instant = toInstant(value) || new Date();
  const zone = zoneOrDefault(timeZone);
  const part = formatter("zone", zone, { timeZoneName: "short" }, "en-AU")
    .formatToParts(instant)
    .find((p) => p.type === "timeZoneName");
  return part?.value || "";
}

/**
 * The customer time format: "1:10 pm" in Sydney time ("" if the value is unusable).
 *
 * @param value      Date | epoch ms | ISO string | Firestore timestamp | "HH:mm"
 * @param options.on           day a bare "HH:mm" belongs to (race date "DD-MM-YYYY", ISO, Date); default today
 * @param options.sourceTimeZone  zone of a bare "HH:mm" (default Brisbane, the tips backend)
 * @param options.timeZone     zone to show (default Australia/Sydney; bad zones fall back to it)
 * @param options.withZone     append " AEDT" / " AEST"
 */
export function formatTimeOfDay(
  value,
  {
    on,
    sourceTimeZone = BACKEND_WALL_CLOCK_TIME_ZONE,
    timeZone = DISPLAY_TIME_ZONE,
    withZone = false,
  } = {},
) {
  const instant =
    typeof value === "string" && HH_MM.test(value.trim())
      ? wallClockToInstant(value, { on, timeZone: sourceTimeZone })
      : toInstant(value);
  if (!instant) return "";

  const zone = zoneOrDefault(timeZone);
  const parts = formatter("time", zone, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(instant);
  const get = (type) => parts.find((p) => p.type === type)?.value || "";
  const period = get("dayPeriod").replace(/\./g, "").toLowerCase();
  const time = `${get("hour")}:${get("minute")} ${period}`;
  return withZone ? `${time} ${timeZoneLabel(instant, zone)}` : time;
}

/**
 * When Race Day tips are out, in the viewer's display zone: 10:00 am Brisbane
 * = "11:00 am AEDT" in daylight saving, "10:00 am AEST" in winter.
 */
export const raceDayTipsTime = (on = new Date()) =>
  formatTimeOfDay("10:00", { on, withZone: true });
